import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import { gradient, misc } from 'chaoskit/src/assets/styles/utility'
import {
  Container,
  Inline,
  ListItem,
  Section,
  SectionTitle,
} from 'chaoskit/src/components'
import { motion } from 'framer-motion'

import BaseLayout from '~layouts/BaseLayout'
import HelmetSEO from '~partials/global/HelmetSEO'
import Hero, { HeroContent } from '~partials/Hero'
import awsReinventLogo from '~media/unscramble/aws-reinvent.svg'
import Image from '~components/Image'
import heroGraph from '~media/unscramble/hero-graph.svg'
import Data from '~components/Data'
import pagerdutyLogo from '~media/logos/pagerduty.svg'
import gremlinFullLogo from '~media/global/logo-full.svg'
import circleCILogo from '~media/logos/circleci.svg'
import dynatraceLogo from '~media/logos/dynatrace.svg'
import HeroLines from '~components/unscramble/HeroLines'
import TextLink from '~components/TextLink'

const SvgGradientDefs = () => (
  <svg viewBox="0 0 0 0" css={[misc.hide]}>
    <defs>
      <linearGradient
        id="primary"
        x1="0%"
        y1="0%"
        x2="100%"
        y2="0%"
        gradientTransform="rotate(270deg)"
      >
        <stop offset="0%" stopColor="#DF49E6" />
        <stop offset="100%" stopColor="#F99738" />
      </linearGradient>
      <linearGradient
        id="secondary"
        x1="0%"
        y1="0%"
        x2="100%"
        y2="0%"
        gradientTransform="rotate(270deg)"
      >
        <stop offset="0%" stopColor="#FA9738" />
        <stop offset="100%" stopColor="#44C751" />
      </linearGradient>
    </defs>
  </svg>
)

const Unscramble = ({
  data: {
    file: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => {
  const theme = useTheme()

  return (
    <BaseLayout
      footerProps={{
        hideCTA: true,
      }}
      siteWrapperProps={{
        css: [gradient.dark(theme)],
      }}
    >
      <SvgGradientDefs />
      <HelmetSEO
        title={pageData.title}
        description={pageData.description}
        noIndex
      />
      <Hero
        css={{
          overflow: 'hidden',
          background: `url(${heroGraph})`,
          maskImage: `linear-gradient(
                    180deg,
                    #fff 80%,
                    transparent
                  )`,
        }}
      >
        <HeroLines
          css={{
            position: 'absolute',
            top: `calc(50% + ${theme.space.large + theme.space.small}px)`,
            left: '50%',
            transform: 'translate(-40%, -50%)',
            maxWidth: 'none',
            zIndex: -1,

            [theme.mq.medium]: {
              transform: 'translate(-50%, -50%)',
            },
          }}
        />
        <HeroContent>
          <Container>
            <div
              css={{
                display: 'grid',
                gap: theme.space.large,
                textAlign: 'center',
                placeContent: 'center',

                [theme.mq.medium]: {
                  gridTemplateColumns: 'repeat(2, auto)',
                },
              }}
            >
              <motion.h1
                initial={{ opacity: 0, x: -theme.space.large }}
                animate={{ opacity: 1, x: 0 }}
                css={{ margin: 0 }}
              >
                Unscrambling
              </motion.h1>
              <Image
                initial={{ opacity: 0, x: theme.space.large }}
                animate={{ opacity: 1, x: 0 }}
                css={{ width: 300 }}
                src={awsReinventLogo}
                alt="AWS:Reinvent"
              />
            </div>
          </Container>
        </HeroContent>
      </Hero>
      <Section className="u-contrast">
        <Container size="small">
          <SectionTitle title={<Data>{pageData.introduction.heading}</Data>} />
        </Container>
      </Section>

      <Section className="u-contrast">
        <Container>
          <Inline
            size="large"
            css={{ justifyContent: 'center', alignItems: 'flex-end' }}
          >
            <ListItem>
              <TextLink href="https://virtual.awsevents.com/sponsor/186983503/PagerDuty">
                <Image
                  src={pagerdutyLogo}
                  css={{ filter: 'brightness(0) invert(1)', width: 200 }}
                  alt="PagerDuty"
                />
              </TextLink>
            </ListItem>
            <ListItem>
              <TextLink href="https://virtual.awsevents.com/sponsor/186983193/Gremlin">
                <Image
                  src={gremlinFullLogo}
                  alt="Gremlin"
                  css={{ width: 160 }}
                />
              </TextLink>
            </ListItem>
            <ListItem>
              <TextLink href="https://virtual.awsevents.com/sponsor/186982803/CircleCI">
                <Image src={circleCILogo} alt="CircleCI" css={{ width: 125 }} />
              </TextLink>
            </ListItem>
            <ListItem>
              <TextLink href="https://virtual.awsevents.com/sponsor/186983093/Dynatrace">
                <Image
                  src={dynatraceLogo}
                  alt="DynaTrace"
                  css={{ width: 225 }}
                />
              </TextLink>
            </ListItem>
          </Inline>
        </Container>
      </Section>
    </BaseLayout>
  )
}

Unscramble.propTypes = {
  data: PropTypes.object.isRequired,
}

export default Unscramble

export const pageQuery = graphql`
  query UnscramblePageData {
    file(name: { eq: "unscramble" }, absolutePath: { regex: "/src/data/" }) {
      childMdx {
        frontmatter {
          title
          description
          introduction {
            heading
          }
        }
      }
    }
  }
`
